import { useEffect, useSyncExternalStore, type ReactNode } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import {
  defaultLocale,
  getLocaleDirection,
  isSupportedLocale,
  keys,
  type PortalLocale,
} from "@/lib/i18n/portal";
import { SellerIntlProvider, useTranslations } from "@/lib/i18n/intl";
import { resolveEntryLocale } from "@/lib/i18n/locale-preference";
import { ToastProvider, useToast } from "@a2z/ui";
import { getPrivateSessionState, subscribePrivateSession } from "@/lib/api/private-session";
import { consumePasswordSavedNotice } from "@/lib/auth/password-saved-notice";
import { useApplyTheme } from "@/shared/theme/useTheme";
import { NativeUpdateBootstrap } from "@/app/native/NativeUpdateBootstrap";

export function getResolvedSellerLocale(value: string | undefined): PortalLocale {
  return value && isSupportedLocale(value) ? value : defaultLocale;
}

export function useLocale() {
  const { locale } = useParams();
  return getResolvedSellerLocale(locale);
}

/**
 * Entry point that carries no locale (`/`, or a path outside every locale
 * segment). Uses the seller's stored choice, falling back to `defaultLocale` —
 * Arabic — for anyone who has never picked one, which is every first visit.
 */
export function LocaleEntryRedirect() {
  return <Navigate to={`/${resolveEntryLocale()}/login`} replace />;
}

/**
 * Catch-all for unmatched paths *inside* a locale segment. Without this, they
 * escape to the router's root `*` route, which is locale-blind and would drop
 * an English user into Arabic.
 */
export function LocaleFallbackRedirect() {
  const { locale } = useParams();
  return <Navigate to={`/${getResolvedSellerLocale(locale)}/login`} replace />;
}

export function SellerLocaleLayout() {
  const { locale } = useParams();
  const location = useLocation();
  const resolvedLocale = getResolvedSellerLocale(locale);
  useApplyTheme();

  // Keep the document root `<html>` in sync with the active locale. Chrome's
  // page-translation heuristic reads the ROOT lang/dir (not the inner
  // `.seller-app` wrapper); leaving it stale makes the browser see an
  // English-declared page full of Arabic text and auto-translate it into
  // garbled MSA. Syncing here keeps the root honest for both `en` and `ar`.
  useEffect(() => {
    document.documentElement.lang = resolvedLocale;
    document.documentElement.dir = getLocaleDirection(resolvedLocale);
  }, [resolvedLocale]);

  if (locale !== resolvedLocale) {
    const suffix = location.pathname.replace(/^\/[^/]+/, "") || "/login";
    return <Navigate to={`/${resolvedLocale}${suffix}${location.search}`} replace />;
  }

  return (
    <SellerIntlProvider locale={resolvedLocale}>
      <LocalizedToastProvider>
        <div className="seller-app" lang={resolvedLocale} dir={getLocaleDirection(resolvedLocale)}>
          <Outlet />
          <PasswordSavedNotice />
          <NativeUpdateBootstrap />
        </div>
      </LocalizedToastProvider>
    </SellerIntlProvider>
  );
}

/**
 * "تم حفظ كلمة المرور" after the first-login change (roundtable 2, B7). The
 * change page does not survive the session rebind (the portal remounts on
 * every session generation, this provider included), so it leaves a one-shot
 * flag (`lib/auth/password-saved-notice.ts`). Read here — above every route,
 * so the toast shows wherever she lands (the store, the quick setup) — once
 * the session has settled and she has left the change page.
 */
function PasswordSavedNotice() {
  const t = useTranslations();
  const toast = useToast();
  const { pathname } = useLocation();
  const session = useSyncExternalStore(subscribePrivateSession, getPrivateSessionState);
  useEffect(() => {
    if (session.changing || !session.storeSlug) return;
    if (pathname.endsWith("/change-password")) return;
    if (consumePasswordSavedNotice()) toast.success(t(keys.auth.passwordSavedToast));
  }, [session.changing, session.storeSlug, pathname, toast, t]);
  return null;
}

/** The toast's close button in the portal's language — the bare provider
 *  defaults to an English "Close" accessible name (roundtable 2, C8). */
function LocalizedToastProvider({ children }: { children: ReactNode }) {
  const t = useTranslations();
  return <ToastProvider closeLabel={t(keys.common.close)}>{children}</ToastProvider>;
}
